import { useEffect, useState } from 'react';

type ToastVariant = 'info' | 'success' | 'error';

interface ToastState {
  msg: string;
  variant: ToastVariant;
  visible: boolean;
}

interface ShowToastOpts {
  ms?: number;
  variant?: ToastVariant;
}

let push: ((msg: string, opts?: ShowToastOpts) => void) | null = null;

/**
 * Mostrar un toast. Acepta opcionalmente `{ ms, variant }`. Para soporte
 * legacy del segundo arg como `number`, también funciona `showToast('msg', 3000)`.
 */
export function showToast(msg: string, opts?: ShowToastOpts | number): void {
  if (typeof opts === 'number') {
    push?.(msg, { ms: opts });
  } else {
    push?.(msg, opts);
  }
}

const VARIANT_BG: Record<ToastVariant, string> = {
  info: 'var(--teal)',
  success: 'var(--btn-primary-bg)',
  error: '#c0392b',
};
const VARIANT_FG: Record<ToastVariant, string> = {
  info: 'white',
  success: 'var(--btn-primary-text)',
  error: 'white',
};

/**
 * Toast host. Una sola instancia por SPA (en main shell o App).
 *
 * Accesibilidad:
 *   - Variants `info`/`success` → role="status" + aria-live="polite"
 *     (no interrumpe lo que el usuario esté haciendo).
 *   - Variant `error` → role="alert" + aria-live="assertive" (interrumpe,
 *     porque algo falló y el user debe enterarse).
 *   - aria-atomic="true" para que el lector lea el mensaje completo, no
 *     solo las palabras nuevas si se actualiza.
 */
export function ToastHost() {
  const [s, setS] = useState<ToastState>({ msg: '', variant: 'info', visible: false });

  useEffect(() => {
    push = (msg, opts) => {
      const variant = opts?.variant ?? inferVariantFromMessage(msg);
      setS({ msg, variant, visible: true });
      window.setTimeout(
        () => setS((x) => ({ ...x, visible: false })),
        opts?.ms ?? (variant === 'error' ? 4000 : 2200),
      );
    };
    return () => { push = null; };
  }, []);

  const isError = s.variant === 'error';

  return (
    <div
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      aria-atomic="true"
      style={{
        position: 'fixed', bottom: 22, left: '50%',
        transform: `translateX(-50%) translateY(${s.visible ? 0 : 8}px)`,
        padding: '10px 18px',
        background: VARIANT_BG[s.variant],
        color: VARIANT_FG[s.variant],
        borderRadius: 'var(--radius-sm)', fontSize: 13,
        fontWeight: 500,
        boxShadow: 'var(--shadow-lg)', zIndex: 'var(--z-toast)',
        opacity: s.visible ? 1 : 0,
        transition: 'opacity 0.2s, transform 0.2s',
        pointerEvents: s.visible ? 'auto' : 'none',
        maxWidth: 'min(440px, calc(100vw - 32px))',
      }}
    >
      {s.msg}
    </div>
  );
}

/**
 * Heurística: si el mensaje empieza con palabras de error/falla, asume
 * variant=error. Esto da semánticos buenos sin que cada caller tenga
 * que pasar variant explícito. Caller puede override con showToast(msg,
 * { variant: 'success' }).
 */
function inferVariantFromMessage(msg: string): ToastVariant {
  const m = msg.toLowerCase();
  if (/(error|falló|no se pudo|fallo|fail|denegad)/i.test(m)) return 'error';
  if (/(guardado|enviado|exito|listo|✓|completado)/i.test(m)) return 'success';
  return 'info';
}
